import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';
import { DropdownOption, GlobalDropdownField } from '../../components/GlobalComponents/GlobalDropdownField';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { GlobalTextArea } from '../../components/GlobalComponents/GlobalTextArea';
import { GlobalButton } from '../../components/GlobalComponents/GlobalButton';
import { GlobalInputField } from '../../components/GlobalComponents/GlobalInputField';
import { useSuperAdminDashboardStore } from '../../stateManagers/stores/superAdminDashboardStore';

interface AddBranchModalProps {
  visible: boolean;
  onHide: () => void;
  onSuccess?: () => void;
  toastRef: React.RefObject<Toast | null>;
}

// ── Static data ────────────────────────────────────────────────────────────────

const INDIAN_STATES: DropdownOption[] = [
  { label: 'Andhra Pradesh', value: 'Andhra Pradesh' },
  { label: 'Arunachal Pradesh', value: 'Arunachal Pradesh' },
  { label: 'Assam', value: 'Assam' },
  { label: 'Bihar', value: 'Bihar' },
  { label: 'Chhattisgarh', value: 'Chhattisgarh' },
  { label: 'Goa', value: 'Goa' },
  { label: 'Gujarat', value: 'Gujarat' },
  { label: 'Haryana', value: 'Haryana' },
  { label: 'Himachal Pradesh', value: 'Himachal Pradesh' },
  { label: 'Jharkhand', value: 'Jharkhand' },
  { label: 'Karnataka', value: 'Karnataka' },
  { label: 'Kerala', value: 'Kerala' },
  { label: 'Madhya Pradesh', value: 'Madhya Pradesh' },
  { label: 'Maharashtra', value: 'Maharashtra' },
  { label: 'Manipur', value: 'Manipur' },
  { label: 'Meghalaya', value: 'Meghalaya' },
  { label: 'Mizoram', value: 'Mizoram' },
  { label: 'Nagaland', value: 'Nagaland' },
  { label: 'Odisha', value: 'Odisha' },
  { label: 'Punjab', value: 'Punjab' },
  { label: 'Rajasthan', value: 'Rajasthan' },
  { label: 'Sikkim', value: 'Sikkim' },
  { label: 'Tamil Nadu', value: 'Tamil Nadu' },
  { label: 'Telangana', value: 'Telangana' },
  { label: 'Tripura', value: 'Tripura' },
  { label: 'Uttar Pradesh', value: 'Uttar Pradesh' },
  { label: 'Uttarakhand', value: 'Uttarakhand' },
  { label: 'West Bengal', value: 'West Bengal' },
  // Union Territories
  { label: 'Delhi', value: 'Delhi' },
  { label: 'Jammu & Kashmir', value: 'Jammu and Kashmir' },
  { label: 'Ladakh', value: 'Ladakh' },
  { label: 'Chandigarh', value: 'Chandigarh' },
  { label: 'Puducherry', value: 'Puducherry' },
  { label: 'Andaman & Nicobar', value: 'Andaman & Nicobar' },
  { label: 'Dadra & Nagar Haveli', value: 'Dadra and Nagar Haveli' },
  { label: 'Daman & Diu', value: 'Daman and Diu' },
  { label: 'Lakshadweep', value: 'Lakshadweep' },
];

const BRANCH_TYPE_OPTIONS: DropdownOption[] = [
  { label: 'Authorised Study Centre (ASC)', value: 'asc' },
  { label: 'Franchise', value: 'franchise' },
  { label: 'Main Branch', value: 'main' },
];

const STATUS_OPTIONS: DropdownOption[] = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
  { label: 'Closed', value: 'closed' },
];

// ── Initial form state ─────────────────────────────────────────────────────────

const INITIAL_FORM = {
  name: '',
  email: '',         // ✅ NEW
  contact_no: '',    // ✅ NEW
  license_start: '',
  license_expiry: '',
  // location
  state: '',
  district: '',
  village: '',
  address: '',
  pincode: '',
  latitude: '',
  longitude: '',
  // branch details
  branch_type: '',
  status: 'active',
  total_pc: '',
  capacity: '',
  opening_time: '',
  closing_time: '',
};

// ── Component ──────────────────────────────────────────────────────────────────

const AddBranchModal: React.FC<AddBranchModalProps> = ({
  visible,
  onHide,
  onSuccess,
  toastRef,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({ ...INITIAL_FORM });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const {
    data: dashboardData,
    loading: dashboardLoading,
    fetchDashboardData,
  } = useSuperAdminDashboardStore();

  useEffect(() => {
    if (visible && !dashboardData) {
      fetchDashboardData();
    }
  }, [visible, dashboardData, fetchDashboardData]);

  const institutes = dashboardData?.institutes || [];
  const defaultInstituteId = institutes.length > 0 ? institutes[0].institute_id : '';

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim())         newErrors.name          = 'Branch name is required';
    if (!formData.branch_type)         newErrors.branch_type   = 'Branch type is required';
    if (!formData.status)              newErrors.status         = 'Status is required';
    if (!formData.license_start)       newErrors.license_start  = 'License start date is required';
    if (!formData.license_expiry)      newErrors.license_expiry = 'License expiry date is required';

    // Location
    if (!formData.state)               newErrors.state    = 'State is required';
    if (!formData.district.trim())     newErrors.district = 'District is required';
    if (!formData.village.trim())      newErrors.village  = 'Village / area is required';
    if (!formData.address.trim())      newErrors.address  = 'Address is required';

    if (!formData.pincode.trim()) {
      newErrors.pincode = 'Pincode is required';
    } else if (!/^\d{6}$/.test(formData.pincode)) {
      newErrors.pincode = 'Pincode must be a valid 6-digit number';
    }

    // Numeric
    if (!formData.total_pc.trim()) {
      newErrors.total_pc = 'Total PC count is required';
    } else if (isNaN(Number(formData.total_pc)) || Number(formData.total_pc) < 0) {
      newErrors.total_pc = 'Total PC must be a valid positive number';
    }
    if (!formData.capacity.trim()) {
      newErrors.capacity = 'Capacity is required';
    } else if (isNaN(Number(formData.capacity)) || Number(formData.capacity) < 0) {
      newErrors.capacity = 'Capacity must be a valid positive number';
    }

    // Date logic
    if (formData.license_start && formData.license_expiry) {
      const start  = new Date(formData.license_start);
      const expiry = new Date(formData.license_expiry);
      if (isNaN(start.getTime()))  newErrors.license_start  = 'Invalid date format';
      if (isNaN(expiry.getTime())) newErrors.license_expiry = 'Invalid date format';
      if (start >= expiry)         newErrors.license_expiry = 'Expiry date must be after start date';
    }

    // Optional geo
    if (formData.latitude && (isNaN(Number(formData.latitude)) || Number(formData.latitude) < -90 || Number(formData.latitude) > 90)) {
      newErrors.latitude = 'Latitude must be between -90 and 90';
    }
    if (formData.longitude && (isNaN(Number(formData.longitude)) || Number(formData.longitude) < -180 || Number(formData.longitude) > 180)) {
      newErrors.longitude = 'Longitude must be between -180 and 180';
    }

    // ✅ NEW: Optional email validation
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Invalid email format';
    }

    // contact_no: if provided, must be exactly 10 digits
    if (formData.contact_no.trim() && !/^\d{10}$/.test(formData.contact_no.trim())) {
      newErrors.contact_no = 'Contact number must be exactly 10 digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      const msg = 'Please fill all required fields correctly';
      setError(msg);
      toastRef.current?.show({
        severity: 'warn',
        summary: 'Validation Error',
        detail: msg,
        life: 4000,
      });
      return;
    }

    if (!defaultInstituteId) {
      const msg = 'No institute found. Please ensure you have at least one institute.';
      setError(msg);
      toastRef.current?.show({
        severity: 'error',
        summary: 'No Institute Found',
        detail: 'Please create an institute first before adding branches.',
        life: 5000,
      });
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload: any = {
        institute_id:   defaultInstituteId,
        name:           formData.name.trim(),
        license_start:  formData.license_start,
        license_expiry: formData.license_expiry,
        // location
        state:          formData.state,
        district:       formData.district.trim(),
        village:        formData.village.trim(),
        address:        formData.address.trim(),
        pincode:        formData.pincode,
        // details
        branch_type:    formData.branch_type,
        status:         formData.status,
        total_pc:       formData.total_pc,
        capacity:       formData.capacity,
      };

      // Optional fields
      if (formData.email.trim())      payload.email        = formData.email.trim();      // ✅ NEW
      if (formData.contact_no.trim()) payload.contact_no   = formData.contact_no.trim(); // ✅ NEW
      if (formData.latitude)          payload.latitude     = formData.latitude;
      if (formData.longitude)         payload.longitude    = formData.longitude;
      if (formData.opening_time)      payload.opening_time = formData.opening_time;
      if (formData.closing_time)      payload.closing_time = formData.closing_time;

      const response = await apiClient.post('/v1/branches', payload);

      if (response.data.success) {
        toastRef.current?.show({
          severity: 'success',
          summary: 'Branch Created',
          detail: `${response.data.data.name} (${response.data.data.branch_code}) created successfully`,
          life: 4000,
        });
        handleClose();
        onSuccess?.();
      } else {
        const msg = response.data.message || 'Failed to create branch';
        setError(msg);
        toastRef.current?.show({
          severity: 'error',
          summary: 'Creation Failed',
          detail: msg,
          life: 5000,
        });
      }
    } catch (err: any) {
      console.error('Failed to create branch:', err);

      let errMsg = 'Failed to create branch';
      if (err.response?.data?.messages && Array.isArray(err.response.data.messages)) {
        errMsg = err.response.data.messages.join(', ');
      } else {
        errMsg =
          err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          errMsg;
      }

      setError(errMsg);
      toastRef.current?.show({
        severity: 'error',
        summary: 'Branch Creation Failed',
        detail: errMsg,
        life: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ ...INITIAL_FORM });
    setErrors({});
    setError(null);
    onHide();
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Dialog
      visible={visible}
      onHide={handleClose}
      style={{ width: '90vw', maxWidth: '1000px' }}
      dismissableMask
      draggable={false}
      pt={{
        root: {
          style: {
            borderRadius: '20px',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          },
        },
        content: {
          style: {
            maxHeight: '85vh',
            overflowY: 'auto',
          },
        },
      }}
    >
      <div>
        {/* ── Header ── */}
        <div style={{
          background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 50%, #0a2f2f 100%)',
          borderRadius: '16px', padding: '24px', marginBottom: '24px',
          color: 'white', position: 'relative', overflow: 'hidden',
        }}>
          <div style={{
            position: 'absolute', top: '-50px', right: '-50px',
            width: '150px', height: '150px',
            background: 'rgba(212, 241, 126, 0.1)',
            borderRadius: '50%', filter: 'blur(40px)',
          }} />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{
              display: 'inline-block', padding: '4px 10px',
              background: 'rgba(212, 241, 126, 0.2)', borderRadius: '16px',
              marginBottom: '8px', fontSize: '0.65rem', fontWeight: '600',
              letterSpacing: '0.5px', textTransform: 'uppercase',
            }}>
              Branch Management
            </div>
            <h2 style={{ margin: '0 0 6px 0', fontSize: '1.4rem', fontWeight: '700', color: 'white' }}>
              Add New Branch
            </h2>
            <p style={{ margin: 0, color: 'rgba(255,255,255,0.8)', fontSize: '0.8rem' }}>
              License number &amp; branch ID are auto-generated based on institute and state
            </p>
          </div>
        </div>

        {/* ── Error ── */}
        {error && (
          <Message severity="error" text={error}
            style={{ marginBottom: '20px', fontSize: '0.9rem', fontWeight: '500' }} />
        )}

        {/* ── Institute loading ── */}
        {dashboardLoading && (
          <div style={{
            background: '#f0f9ff', border: '1px solid #0f3d3e20',
            borderRadius: '12px', padding: '20px', marginBottom: '20px', textAlign: 'center',
          }}>
            <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem', color: '#0f3d3e' }} />
            <p style={{ marginTop: '12px', color: '#666' }}>Loading institute information...</p>
          </div>
        )}

        {/* ── Institute banner ── */}
        {!dashboardLoading && institutes.length > 0 && (
          <div style={{
            background: '#f0f9ff', border: '1px solid #0f3d3e20',
            borderRadius: '12px', padding: '16px', marginBottom: '20px',
            display: 'flex', alignItems: 'center', gap: '12px',
          }}>
            <div style={{
              width: '40px', height: '40px', background: '#0f3d3e',
              borderRadius: '10px', display: 'flex', alignItems: 'center',
              justifyContent: 'center', flexShrink: 0,
            }}>
              <i className="pi pi-building" style={{ color: '#d4f17e', fontSize: '1.2rem' }} />
            </div>
            <div style={{ flex: 1 }}>
              <p style={{
                margin: '0 0 4px 0', fontSize: '0.75rem', color: '#666',
                fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px',
              }}>
                Creating Branch For
              </p>
              <p style={{ margin: 0, fontSize: '1rem', fontWeight: '600', color: '#0f3d3e' }}>
                {institutes[0].name}
              </p>
            </div>
            <div style={{
              padding: '6px 12px', background: 'rgba(15,61,62,0.08)',
              borderRadius: '8px', fontSize: '0.75rem', color: '#0f3d3e', fontWeight: '600',
            }}>
              ID auto-generated
            </div>
          </div>
        )}

        {/* ── No institute warning ── */}
        {!dashboardLoading && institutes.length === 0 && (
          <Message severity="warn"
            text="No institute found. Please create an institute first before adding branches."
            style={{ marginBottom: '20px' }} />
        )}

        {/* ── Form ── */}
        {!dashboardLoading && (
          <div style={{
            background: 'white', borderRadius: '12px', padding: '20px',
            marginBottom: '20px', border: '1px solid #f0f0f0',
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

              {/* ── Section 1: Basic Information ── */}
              <Section title="Basic Information">
                <GlobalInputField
                  label="Branch Name"
                  required
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="e.g., Main Branch - Delhi"
                  icon="pi pi-building"
                  error={errors.name}
                />

                {/* ✅ NEW: Contact info row */}
                <TwoCol>
                  <GlobalInputField
                    label="Email Address"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="e.g., branch@institute.com"
                    icon="pi pi-envelope"
                    error={errors.email}
                  />
                  <GlobalInputField
                    label="Contact Number"
                    value={formData.contact_no}
                    onChange={(e) => handleInputChange('contact_no', e.target.value)}
                    placeholder="e.g., 9876543210"
                    icon="pi pi-phone"
                    error={errors.contact_no}
                    maxLength={10}
                  />
                </TwoCol>

                <TwoCol>
                  <GlobalDropdownField
                    label="Branch Type"
                    required
                    value={formData.branch_type}
                    onChange={(val) => handleInputChange('branch_type', val)}
                    options={BRANCH_TYPE_OPTIONS}
                    placeholder="Select branch type"
                    icon="pi pi-sitemap"
                    error={errors.branch_type}
                  />

                  <GlobalDropdownField
                    label="Status"
                    required
                    value={formData.status}
                    onChange={(val) => handleInputChange('status', val)}
                    options={STATUS_OPTIONS}
                    placeholder="Select status"
                    icon="pi pi-check-circle"
                    error={errors.status}
                  />
                </TwoCol>

                <TwoCol>
                  <GlobalInputField
                    label="License Start Date"
                    required
                    type="date"
                    value={formData.license_start}
                    onChange={(e) => handleInputChange('license_start', e.target.value)}
                    icon="pi pi-calendar"
                    error={errors.license_start}
                  />

                  <GlobalInputField
                    label="License Expiry Date"
                    required
                    type="date"
                    value={formData.license_expiry}
                    onChange={(e) => handleInputChange('license_expiry', e.target.value)}
                    icon="pi pi-calendar"
                    error={errors.license_expiry}
                  />
                </TwoCol>
              </Section>

              {/* ── Section 2: Location ── */}
              <Section title="Location Information">
                <TwoCol>
                  <GlobalDropdownField
                    label="State"
                    required
                    value={formData.state}
                    onChange={(val) => handleInputChange('state', val)}
                    options={INDIAN_STATES}
                    placeholder="Select state"
                    icon="pi pi-map"
                    error={errors.state}
                    filter
                  />

                  <GlobalInputField
                    label="District"
                    required
                    value={formData.district}
                    onChange={(e) => handleInputChange('district', e.target.value)}
                    placeholder="e.g., South Delhi"
                    icon="pi pi-map-marker"
                    error={errors.district}
                  />
                </TwoCol>

                <GlobalInputField
                  label="Village / Area / Locality"
                  required
                  value={formData.village}
                  onChange={(e) => handleInputChange('village', e.target.value)}
                  placeholder="e.g., Lajpat Nagar"
                  icon="pi pi-home"
                  error={errors.village}
                />

                <GlobalTextArea
                  label="Full Address"
                  required
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  placeholder="Enter complete address"
                  rows={3}
                  error={errors.address}
                />

                <TwoCol>
                  <GlobalInputField
                    label="Pincode"
                    required
                    value={formData.pincode}
                    onChange={(e) => handleInputChange('pincode', e.target.value)}
                    placeholder="e.g., 110001"
                    icon="pi pi-map-marker"
                    error={errors.pincode}
                    maxLength={6}
                  />

                  <TwoCol>
                    <GlobalInputField
                      label="Latitude"
                      type="number"
                      value={formData.latitude}
                      onChange={(e) => handleInputChange('latitude', e.target.value)}
                      placeholder="e.g., 28.6315"
                      error={errors.latitude}
                    />
                    <GlobalInputField
                      label="Longitude"
                      type="number"
                      value={formData.longitude}
                      onChange={(e) => handleInputChange('longitude', e.target.value)}
                      placeholder="e.g., 77.2167"
                      error={errors.longitude}
                    />
                  </TwoCol>
                </TwoCol>
              </Section>

              {/* ── Section 3: Capacity & Hours ── */}
              <Section title="Capacity & Operating Hours">
                <TwoCol>
                  <GlobalInputField
                    label="Total PCs"
                    required
                    type="number"
                    value={formData.total_pc}
                    onChange={(e) => handleInputChange('total_pc', e.target.value)}
                    placeholder="e.g., 50"
                    icon="pi pi-desktop"
                    error={errors.total_pc}
                  />

                  <GlobalInputField
                    label="Capacity (Students)"
                    required
                    type="number"
                    value={formData.capacity}
                    onChange={(e) => handleInputChange('capacity', e.target.value)}
                    placeholder="e.g., 200"
                    icon="pi pi-users"
                    error={errors.capacity}
                  />
                </TwoCol>

                <TwoCol>
                  <GlobalInputField
                    label="Opening Time"
                    type="time"
                    value={formData.opening_time}
                    onChange={(e) => handleInputChange('opening_time', e.target.value)}
                    icon="pi pi-clock"
                  />
                  <GlobalInputField
                    label="Closing Time"
                    type="time"
                    value={formData.closing_time}
                    onChange={(e) => handleInputChange('closing_time', e.target.value)}
                    icon="pi pi-clock"
                  />
                </TwoCol>
              </Section>

            </div>
          </div>
        )}

        {/* ── Actions ── */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <GlobalButton
            label="Cancel"
            variant="outline"
            icon="pi pi-times"
            onClick={handleClose}
            disabled={loading}
          />
          <GlobalButton
            label={loading ? 'Creating Branch...' : 'Create Branch'}
            variant="primary"
            icon={loading ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
            onClick={handleSubmit}
            disabled={loading || dashboardLoading || institutes.length === 0}
            loading={loading}
          />
        </div>
      </div>
    </Dialog>
  );
};

// ── Small layout helpers ────────────────────────────────────────────────────────

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{
    padding: '16px', background: '#f9f9f9',
    borderRadius: '8px', border: '1px solid #e0e0e0',
  }}>
    <h3 style={{ margin: '0 0 16px 0', fontSize: '0.95rem', fontWeight: '600', color: '#0f3d3e' }}>
      {title}
    </h3>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {children}
    </div>
  </div>
);

const TwoCol: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
    {children}
  </div>
);

export default AddBranchModal;


// import React, { useState, useEffect } from 'react';
// import { Dialog } from 'primereact/dialog';
// import { Message } from 'primereact/message';
// import { Toast } from 'primereact/toast';
// import { DropdownOption, GlobalDropdownField } from '../../components/GlobalComponents/GlobalDropdownField';
// import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
// import { GlobalTextArea } from '../../components/GlobalComponents/GlobalTextArea';
// import { GlobalButton } from '../../components/GlobalComponents/GlobalButton';
// import { GlobalInputField } from '../../components/GlobalComponents/GlobalInputField';
// import { useSuperAdminDashboardStore } from '../../stateManagers/stores/superAdminDashboardStore';

// interface AddBranchModalProps {
//   visible: boolean;
//   onHide: () => void;
//   onSuccess?: () => void;
//   toastRef: React.RefObject<Toast | null>;
// }

// // ── Static data ────────────────────────────────────────────────────────────────

// const INDIAN_STATES: DropdownOption[] = [
//   { label: 'Andhra Pradesh', value: 'Andhra Pradesh' },
//   { label: 'Arunachal Pradesh', value: 'Arunachal Pradesh' },
//   { label: 'Assam', value: 'Assam' },
//   { label: 'Bihar', value: 'Bihar' },
//   { label: 'Chhattisgarh', value: 'Chhattisgarh' },
//   { label: 'Goa', value: 'Goa' },
//   { label: 'Gujarat', value: 'Gujarat' },
//   { label: 'Haryana', value: 'Haryana' },
//   { label: 'Himachal Pradesh', value: 'Himachal Pradesh' },
//   { label: 'Jharkhand', value: 'Jharkhand' },
//   { label: 'Karnataka', value: 'Karnataka' },
//   { label: 'Kerala', value: 'Kerala' },
//   { label: 'Madhya Pradesh', value: 'Madhya Pradesh' },
//   { label: 'Maharashtra', value: 'Maharashtra' },
//   { label: 'Manipur', value: 'Manipur' },
//   { label: 'Meghalaya', value: 'Meghalaya' },
//   { label: 'Mizoram', value: 'Mizoram' },
//   { label: 'Nagaland', value: 'Nagaland' },
//   { label: 'Odisha', value: 'Odisha' },
//   { label: 'Punjab', value: 'Punjab' },
//   { label: 'Rajasthan', value: 'Rajasthan' },
//   { label: 'Sikkim', value: 'Sikkim' },
//   { label: 'Tamil Nadu', value: 'Tamil Nadu' },
//   { label: 'Telangana', value: 'Telangana' },
//   { label: 'Tripura', value: 'Tripura' },
//   { label: 'Uttar Pradesh', value: 'Uttar Pradesh' },
//   { label: 'Uttarakhand', value: 'Uttarakhand' },
//   { label: 'West Bengal', value: 'West Bengal' },
//   // Union Territories
//   { label: 'Delhi', value: 'Delhi' },
//   { label: 'Jammu & Kashmir', value: 'Jammu and Kashmir' },
//   { label: 'Ladakh', value: 'Ladakh' },
//   { label: 'Chandigarh', value: 'Chandigarh' },
//   { label: 'Puducherry', value: 'Puducherry' },
//   { label: 'Andaman & Nicobar', value: 'Andaman & Nicobar' },
//   { label: 'Dadra & Nagar Haveli', value: 'Dadra and Nagar Haveli' },
//   { label: 'Daman & Diu', value: 'Daman and Diu' },
//   { label: 'Lakshadweep', value: 'Lakshadweep' },
// ];

// const BRANCH_TYPE_OPTIONS: DropdownOption[] = [
//   { label: 'Authorised Study Centre (ASC)', value: 'asc' },
//   { label: 'Franchise', value: 'franchise' },
//   { label: 'Main Branch', value: 'main' },
// ];

// const STATUS_OPTIONS: DropdownOption[] = [
//   { label: 'Active', value: 'active' },
//   { label: 'Inactive', value: 'inactive' },
//   { label: 'Closed', value: 'closed' },
// ];

// // ── Initial form state ─────────────────────────────────────────────────────────

// const INITIAL_FORM = {
//   name: '',
//   license_start: '',
//   license_expiry: '',
//   // location
//   state: '',
//   district: '',
//   village: '',
//   address: '',
//   pincode: '',
//   latitude: '',
//   longitude: '',
//   // branch details
//   branch_type: '',
//   status: 'active',
//   total_pc: '',
//   capacity: '',
//   opening_time: '',
//   closing_time: '',
// };

// // ── Component ──────────────────────────────────────────────────────────────────

// const AddBranchModal: React.FC<AddBranchModalProps> = ({
//   visible,
//   onHide,
//   onSuccess,
//   toastRef,
// }) => {
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState<string | null>(null);
//   const [formData, setFormData] = useState({ ...INITIAL_FORM });
//   const [errors, setErrors] = useState<Record<string, string>>({});

//   const {
//     data: dashboardData,
//     loading: dashboardLoading,
//     fetchDashboardData,
//   } = useSuperAdminDashboardStore();

//   useEffect(() => {
//     if (visible && !dashboardData) {
//       fetchDashboardData();
//     }
//   }, [visible, dashboardData, fetchDashboardData]);

//   const institutes = dashboardData?.institutes || [];
//   const defaultInstituteId = institutes.length > 0 ? institutes[0].institute_id : '';

//   // ── Handlers ───────────────────────────────────────────────────────────────

//   const handleInputChange = (field: string, value: any) => {
//     setFormData(prev => ({ ...prev, [field]: value }));
//     if (errors[field]) {
//       setErrors(prev => ({ ...prev, [field]: '' }));
//     }
//   };

//   const validateForm = (): boolean => {
//     const newErrors: Record<string, string> = {};

//     if (!formData.name.trim())         newErrors.name         = 'Branch name is required';
//     if (!formData.branch_type)         newErrors.branch_type  = 'Branch type is required';
//     if (!formData.status)              newErrors.status        = 'Status is required';
//     if (!formData.license_start)       newErrors.license_start = 'License start date is required';
//     if (!formData.license_expiry)      newErrors.license_expiry = 'License expiry date is required';

//     // Location
//     if (!formData.state)               newErrors.state    = 'State is required';
//     if (!formData.district.trim())     newErrors.district = 'District is required';
//     if (!formData.village.trim())      newErrors.village  = 'Village / area is required';
//     if (!formData.address.trim())      newErrors.address  = 'Address is required';

//     if (!formData.pincode.trim()) {
//       newErrors.pincode = 'Pincode is required';
//     } else if (!/^\d{6}$/.test(formData.pincode)) {
//       newErrors.pincode = 'Pincode must be a valid 6-digit number';
//     }

//     // Numeric
//     if (!formData.total_pc.trim()) {
//       newErrors.total_pc = 'Total PC count is required';
//     } else if (isNaN(Number(formData.total_pc)) || Number(formData.total_pc) < 0) {
//       newErrors.total_pc = 'Total PC must be a valid positive number';
//     }
//     if (!formData.capacity.trim()) {
//       newErrors.capacity = 'Capacity is required';
//     } else if (isNaN(Number(formData.capacity)) || Number(formData.capacity) < 0) {
//       newErrors.capacity = 'Capacity must be a valid positive number';
//     }

//     // Date logic
//     if (formData.license_start && formData.license_expiry) {
//       const start  = new Date(formData.license_start);
//       const expiry = new Date(formData.license_expiry);
//       if (isNaN(start.getTime()))  newErrors.license_start  = 'Invalid date format';
//       if (isNaN(expiry.getTime())) newErrors.license_expiry = 'Invalid date format';
//       if (start >= expiry)         newErrors.license_expiry = 'Expiry date must be after start date';
//     }

//     // Optional geo
//     if (formData.latitude && (isNaN(Number(formData.latitude)) || Number(formData.latitude) < -90 || Number(formData.latitude) > 90)) {
//       newErrors.latitude = 'Latitude must be between -90 and 90';
//     }
//     if (formData.longitude && (isNaN(Number(formData.longitude)) || Number(formData.longitude) < -180 || Number(formData.longitude) > 180)) {
//       newErrors.longitude = 'Longitude must be between -180 and 180';
//     }

//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   const handleSubmit = async () => {
//     if (!validateForm()) {
//       const msg = 'Please fill all required fields correctly';
//       setError(msg);
//       toastRef.current?.show({
//         severity: 'warn',
//         summary: 'Validation Error',
//         detail: msg,
//         life: 4000,
//       });
//       return;
//     }

//     if (!defaultInstituteId) {
//       const msg = 'No institute found. Please ensure you have at least one institute.';
//       setError(msg);
//       toastRef.current?.show({
//         severity: 'error',
//         summary: 'No Institute Found',
//         detail: 'Please create an institute first before adding branches.',
//         life: 5000,
//       });
//       return;
//     }

//     setLoading(true);
//     setError(null);

//     try {
//       const payload: any = {
//         institute_id:   defaultInstituteId,
//         name:           formData.name.trim(),
//         license_start:  formData.license_start,
//         license_expiry: formData.license_expiry,
//         // location
//         state:          formData.state,
//         district:       formData.district.trim(),
//         village:        formData.village.trim(),
//         address:        formData.address.trim(),
//         pincode:        formData.pincode,
//         // details
//         branch_type:    formData.branch_type,
//         status:         formData.status,
//         total_pc:       formData.total_pc,
//         capacity:       formData.capacity,
//       };

//       // Optional fields
//       if (formData.latitude)      payload.latitude     = formData.latitude;
//       if (formData.longitude)     payload.longitude    = formData.longitude;
//       if (formData.opening_time)  payload.opening_time = formData.opening_time;
//       if (formData.closing_time)  payload.closing_time = formData.closing_time;

//       const response = await apiClient.post('/v1/branches', payload);

//       if (response.data.success) {
//         toastRef.current?.show({
//           severity: 'success',
//           summary: 'Branch Created',
//           detail: `${response.data.data.name} (${response.data.data.branch_code}) created successfully`,
//           life: 4000,
//         });
//         handleClose();
//         onSuccess?.();
//       } else {
//         const msg = response.data.message || 'Failed to create branch';
//         setError(msg);
//         toastRef.current?.show({
//           severity: 'error',
//           summary: 'Creation Failed',
//           detail: msg,
//           life: 5000,
//         });
//       }
//     } catch (err: any) {
//       console.error('Failed to create branch:', err);

//       let errMsg = 'Failed to create branch';
//       if (err.response?.data?.messages && Array.isArray(err.response.data.messages)) {
//         errMsg = err.response.data.messages.join(', ');
//       } else {
//         errMsg =
//           err.response?.data?.error ||
//           err.response?.data?.message ||
//           err.message ||
//           errMsg;
//       }

//       setError(errMsg);
//       toastRef.current?.show({
//         severity: 'error',
//         summary: 'Branch Creation Failed',
//         detail: errMsg,
//         life: 5000,
//       });
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleClose = () => {
//     setFormData({ ...INITIAL_FORM });
//     setErrors({});
//     setError(null);
//     onHide();
//   };

//   // ── Render ─────────────────────────────────────────────────────────────────

//   return (
//     <Dialog
//       visible={visible}
//       onHide={handleClose}
//       style={{ width: '90vw', maxWidth: '1000px' }}
//       dismissableMask
//       draggable={false}
//       pt={{
//         root: {
//           style: {
//             borderRadius: '20px',
//             overflow: 'hidden',
//             boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
//           },
//         },
//         content: {
//           style: {
//             maxHeight: '85vh',
//             overflowY: 'auto',
//           },
//         },
//       }}
//     >
//       <div>
//         {/* ── Header ── */}
//         <div style={{
//           background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 50%, #0a2f2f 100%)',
//           borderRadius: '16px',
//           padding: '24px',
//           marginBottom: '24px',
//           color: 'white',
//           position: 'relative',
//           overflow: 'hidden',
//         }}>
//           <div style={{
//             position: 'absolute', top: '-50px', right: '-50px',
//             width: '150px', height: '150px',
//             background: 'rgba(212, 241, 126, 0.1)',
//             borderRadius: '50%', filter: 'blur(40px)',
//           }} />
//           <div style={{ position: 'relative', zIndex: 1 }}>
//             <div style={{
//               display: 'inline-block', padding: '4px 10px',
//               background: 'rgba(212, 241, 126, 0.2)', borderRadius: '16px',
//               marginBottom: '8px', fontSize: '0.65rem', fontWeight: '600',
//               letterSpacing: '0.5px', textTransform: 'uppercase',
//             }}>
//               Branch Management
//             </div>
//             <h2 style={{ margin: '0 0 6px 0', fontSize: '1.4rem', fontWeight: '700', color: 'white' }}>
//               Add New Branch
//             </h2>
//             <p style={{ margin: 0, color: 'rgba(255,255,255,0.8)', fontSize: '0.8rem' }}>
//               License number &amp; branch ID are auto-generated based on institute and state
//             </p>
//           </div>
//         </div>

//         {/* ── Error ── */}
//         {error && (
//           <Message severity="error" text={error}
//             style={{ marginBottom: '20px', fontSize: '0.9rem', fontWeight: '500' }} />
//         )}

//         {/* ── Institute loading ── */}
//         {dashboardLoading && (
//           <div style={{
//             background: '#f0f9ff', border: '1px solid #0f3d3e20',
//             borderRadius: '12px', padding: '20px', marginBottom: '20px', textAlign: 'center',
//           }}>
//             <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem', color: '#0f3d3e' }} />
//             <p style={{ marginTop: '12px', color: '#666' }}>Loading institute information...</p>
//           </div>
//         )}

//         {/* ── Institute banner ── */}
//         {!dashboardLoading && institutes.length > 0 && (
//           <div style={{
//             background: '#f0f9ff', border: '1px solid #0f3d3e20',
//             borderRadius: '12px', padding: '16px', marginBottom: '20px',
//             display: 'flex', alignItems: 'center', gap: '12px',
//           }}>
//             <div style={{
//               width: '40px', height: '40px', background: '#0f3d3e',
//               borderRadius: '10px', display: 'flex', alignItems: 'center',
//               justifyContent: 'center', flexShrink: 0,
//             }}>
//               <i className="pi pi-building" style={{ color: '#d4f17e', fontSize: '1.2rem' }} />
//             </div>
//             <div style={{ flex: 1 }}>
//               <p style={{
//                 margin: '0 0 4px 0', fontSize: '0.75rem', color: '#666',
//                 fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px',
//               }}>
//                 Creating Branch For
//               </p>
//               <p style={{ margin: 0, fontSize: '1rem', fontWeight: '600', color: '#0f3d3e' }}>
//                 {institutes[0].name}
//               </p>
//             </div>
//             <div style={{
//               padding: '6px 12px', background: 'rgba(15,61,62,0.08)',
//               borderRadius: '8px', fontSize: '0.75rem', color: '#0f3d3e', fontWeight: '600',
//             }}>
//               ID auto-generated
//             </div>
//           </div>
//         )}

//         {/* ── No institute warning ── */}
//         {!dashboardLoading && institutes.length === 0 && (
//           <Message severity="warn"
//             text="No institute found. Please create an institute first before adding branches."
//             style={{ marginBottom: '20px' }} />
//         )}

//         {/* ── Form ── */}
//         {!dashboardLoading && (
//           <div style={{
//             background: 'white', borderRadius: '12px', padding: '20px',
//             marginBottom: '20px', border: '1px solid #f0f0f0',
//           }}>
//             <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

//               {/* ── Section 1: Basic Information ── */}
//               <Section title="Basic Information">
//                 <GlobalInputField
//                   label="Branch Name"
//                   required
//                   value={formData.name}
//                   onChange={(e) => handleInputChange('name', e.target.value)}
//                   placeholder="e.g., Main Branch - Delhi"
//                   icon="pi pi-building"
//                   error={errors.name}
//                 />

//                 <TwoCol>
//                   <GlobalDropdownField
//                     label="Branch Type"
//                     required
//                     value={formData.branch_type}
//                     onChange={(val) => handleInputChange('branch_type', val)}
//                     options={BRANCH_TYPE_OPTIONS}
//                     placeholder="Select branch type"
//                     icon="pi pi-sitemap"
//                     error={errors.branch_type}
//                   />

//                   <GlobalDropdownField
//                     label="Status"
//                     required
//                     value={formData.status}
//                     onChange={(val) => handleInputChange('status', val)}
//                     options={STATUS_OPTIONS}
//                     placeholder="Select status"
//                     icon="pi pi-check-circle"
//                     error={errors.status}
//                   />
//                 </TwoCol>

//                 <TwoCol>
//                   <GlobalInputField
//                     label="License Start Date"
//                     required
//                     type="date"
//                     value={formData.license_start}
//                     onChange={(e) => handleInputChange('license_start', e.target.value)}
//                     icon="pi pi-calendar"
//                     error={errors.license_start}
//                   />

//                   <GlobalInputField
//                     label="License Expiry Date"
//                     required
//                     type="date"
//                     value={formData.license_expiry}
//                     onChange={(e) => handleInputChange('license_expiry', e.target.value)}
//                     icon="pi pi-calendar"
//                     error={errors.license_expiry}
//                   />
//                 </TwoCol>
//               </Section>

//               {/* ── Section 2: Location ── */}
//               <Section title="Location Information">
//                 <TwoCol>
//                   <GlobalDropdownField
//                     label="State"
//                     required
//                     value={formData.state}
//                     onChange={(val) => handleInputChange('state', val)}
//                     options={INDIAN_STATES}
//                     placeholder="Select state"
//                     icon="pi pi-map"
//                     error={errors.state}
//                     filter
//                   />

//                   <GlobalInputField
//                     label="District"
//                     required
//                     value={formData.district}
//                     onChange={(e) => handleInputChange('district', e.target.value)}
//                     placeholder="e.g., South Delhi"
//                     icon="pi pi-map-marker"
//                     error={errors.district}
//                   />
//                 </TwoCol>

//                 <GlobalInputField
//                   label="Village / Area / Locality"
//                   required
//                   value={formData.village}
//                   onChange={(e) => handleInputChange('village', e.target.value)}
//                   placeholder="e.g., Lajpat Nagar"
//                   icon="pi pi-home"
//                   error={errors.village}
//                 />

//                 <GlobalTextArea
//                   label="Full Address"
//                   required
//                   value={formData.address}
//                   onChange={(e) => handleInputChange('address', e.target.value)}
//                   placeholder="Enter complete address"
//                   rows={3}
//                   error={errors.address}
//                 />

//                 <TwoCol>
//                   <GlobalInputField
//                     label="Pincode"
//                     required
//                     value={formData.pincode}
//                     onChange={(e) => handleInputChange('pincode', e.target.value)}
//                     placeholder="e.g., 110001"
//                     icon="pi pi-map-marker"
//                     error={errors.pincode}
//                     maxLength={6}
//                   />

//                   <TwoCol>
//                     <GlobalInputField
//                       label="Latitude"
//                       type="number"
//                       value={formData.latitude}
//                       onChange={(e) => handleInputChange('latitude', e.target.value)}
//                       placeholder="e.g., 28.6315"
//                       error={errors.latitude}
//                     />
//                     <GlobalInputField
//                       label="Longitude"
//                       type="number"
//                       value={formData.longitude}
//                       onChange={(e) => handleInputChange('longitude', e.target.value)}
//                       placeholder="e.g., 77.2167"
//                       error={errors.longitude}
//                     />
//                   </TwoCol>
//                 </TwoCol>
//               </Section>

//               {/* ── Section 3: Capacity & Hours ── */}
//               <Section title="Capacity & Operating Hours">
//                 <TwoCol>
//                   <GlobalInputField
//                     label="Total PCs"
//                     required
//                     type="number"
//                     value={formData.total_pc}
//                     onChange={(e) => handleInputChange('total_pc', e.target.value)}
//                     placeholder="e.g., 50"
//                     icon="pi pi-desktop"
//                     error={errors.total_pc}
//                   />

//                   <GlobalInputField
//                     label="Capacity (Students)"
//                     required
//                     type="number"
//                     value={formData.capacity}
//                     onChange={(e) => handleInputChange('capacity', e.target.value)}
//                     placeholder="e.g., 200"
//                     icon="pi pi-users"
//                     error={errors.capacity}
//                   />
//                 </TwoCol>

//                 <TwoCol>
//                   <GlobalInputField
//                     label="Opening Time"
//                     type="time"
//                     value={formData.opening_time}
//                     onChange={(e) => handleInputChange('opening_time', e.target.value)}
//                     icon="pi pi-clock"
//                   />
//                   <GlobalInputField
//                     label="Closing Time"
//                     type="time"
//                     value={formData.closing_time}
//                     onChange={(e) => handleInputChange('closing_time', e.target.value)}
//                     icon="pi pi-clock"
//                   />
//                 </TwoCol>
//               </Section>

//             </div>
//           </div>
//         )}

//         {/* ── Actions ── */}
//         <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
//           <GlobalButton
//             label="Cancel"
//             variant="outline"
//             icon="pi pi-times"
//             onClick={handleClose}
//             disabled={loading}
//           />
//           <GlobalButton
//             label={loading ? 'Creating Branch...' : 'Create Branch'}
//             variant="primary"
//             icon={loading ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
//             onClick={handleSubmit}
//             disabled={loading || dashboardLoading || institutes.length === 0}
//             loading={loading}
//           />
//         </div>
//       </div>
//     </Dialog>
//   );
// };

// // ── Small layout helpers (keeps JSX clean) ─────────────────────────────────────

// const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
//   <div style={{
//     padding: '16px', background: '#f9f9f9',
//     borderRadius: '8px', border: '1px solid #e0e0e0',
//   }}>
//     <h3 style={{ margin: '0 0 16px 0', fontSize: '0.95rem', fontWeight: '600', color: '#0f3d3e' }}>
//       {title}
//     </h3>
//     <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
//       {children}
//     </div>
//   </div>
// );

// const TwoCol: React.FC<{ children: React.ReactNode }> = ({ children }) => (
//   <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
//     {children}
//   </div>
// );

// export default AddBranchModal;